import React, { FormEvent, useState, useContext, MouseEvent, useEffect } from 'react';
import { CouponContext } from '../../contexts/CouponContext';
import Logo from '../../components/Logo';
import { FormWrapper, Header, Label, LabelWrapper, Message, ContentWrapper, Value } from './styled';
import { Container, LogoWrapper, Step } from '../../components/FormElements';
import { FormButton } from '../../components/FormButton';
import { CSVLink } from 'react-csv';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import createCoupon, { CreateCouponResponseBody } from '../../apis/createCoupon';
import { Data } from 'react-csv/components/CommonPropTypes';
import { getExpiredTime, getToken, removeToken } from '../../utils/auth';

const CouponConfirm = (): React.ReactElement => {
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codesCreated, setCodesCreated] = useState<string[]>([]);
  const { couponCode, couponDetails } = useContext(CouponContext);
  const couponDetail = couponDetails ? couponDetails[0] : null;
  const navigate = useNavigate();
  const csvData: string[] | Data = [['Coupon Code']];
  const generateCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (couponDetails) {
      setLoading(true);
      const results = await Promise.all(couponDetails.map(createCoupon));
      const codesCreated = results
        .filter((it): it is CreateCouponResponseBody => !!it?.id)
        .map((it) => it.resource.code);

      setCodesCreated(codesCreated);
      setCompleted(true);
    } else if (!couponCode) {
      navigate('/form');
    }
    setLoading(false);
  };

  useEffect(() => {
    function fetchToken() {
      const token = getToken();
      const tokenExpired = Date.now() - Number(getExpiredTime()) > 0 ? true : false;
      if (!token || tokenExpired) {
        removeToken();
        navigate('/');
      }
    }
    fetchToken();
  }, []);

  return (
    <>
      <Container>
        <FormWrapper onSubmit={(event: FormEvent<HTMLFormElement>) => generateCode(event)}>
          <LogoWrapper>
            <Logo />
            <Step>3/3</Step>
          </LogoWrapper>
          <Header>Coupon Details</Header>
          <ContentWrapper>
            <LabelWrapper>
              <Label>Type</Label>
              <Value>{couponDetail?.type ?? 'None'}</Value>
            </LabelWrapper>
            <LabelWrapper>
              <Label>Value</Label>
              <Value>{couponDetail?.value ?? 'None'}</Value>
            </LabelWrapper>
            <LabelWrapper>
              <Label>Min. Amount</Label>
              <Value>{couponDetail?.rules.amount ?? 'None'}</Value>
            </LabelWrapper>
            <LabelWrapper>
              <Label>Route</Label>
              <Value>{couponDetail?.rules.route ?? 'None'}</Value>
            </LabelWrapper>
            {couponDetail?.rules.route === 'route' ? (
              <>
                <LabelWrapper>
                  <Label>From</Label>
                  <Value>{couponDetail ? couponDetail.rules.from : 'None'}</Value>
                </LabelWrapper>
                <LabelWrapper>
                  <Label>To</Label>
                  <Value>{couponDetail ? couponDetail.rules.to : 'None'}</Value>
                </LabelWrapper>
              </>
            ) : null}
            <LabelWrapper>
              <Label>Start Date</Label>
              <Value>{couponDetail?.startAt ?? 'None'}</Value>
            </LabelWrapper>
            <LabelWrapper>
              <Label>Expired Date</Label>
              <Value>{couponDetail?.expiresAt ?? 'None'}</Value>
            </LabelWrapper>
            <LabelWrapper>
              <Label>Success</Label>
              <Value>{`${codesCreated.length} / ${couponDetails?.length}`}</Value>
            </LabelWrapper>
          </ContentWrapper>

          <Message> It will take some time for the coupons to be created</Message>
          {completed ? (
            <FormButton
              variant="primary"
              size="sm"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate('/form');
              }}
            >
              Generate New Coupon code
            </FormButton>
          ) : (
            <FormButton variant="primary" size="sm" type="submit" loading={loading}>
              Create Coupons
            </FormButton>
          )}
          {completed ? (
            <CSVLink
              data={csvData.concat(codesCreated.map((it) => [it]))}
              filename={'coupon code.csv'}
            >
              <FormButton variant="default" size="sm" disabled={completed ? false : true}>
                Download Coupons
              </FormButton>
            </CSVLink>
          ) : (
            <FormButton
              variant="primary"
              size="sm"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate('/detail');
              }}
            >
              Back
            </FormButton>
          )}
        </FormWrapper>
      </Container>
    </>
  );
};

export default CouponConfirm;
